import { Box, Sheet, Typography } from '@mui/joy';

/**
 * Подсказка о том, как шапка меняет раскладку профиля.
 *
 * НЕ кликабельна намеренно: это не переключатель, а объяснение. Раскладку
 * выбирает не отдельная настройка, а наличие обложки, и человеку достаточно
 * понимать связь — «уберу шапку, профиль станет строкой». Сделать макеты
 * кнопками значило бы соврать: нажатие ничего не изменит, менять надо саму
 * обложку ниже.
 *
 * Рисуется примитивами, а не картинкой: два макета обязаны следовать за темой и
 * не расходиться с настоящим профилем после его правок.
 *
 * @param {boolean} hasCap - стоит ли сейчас обложка (подсветит текущий вариант)
 */
export default function ProfileLayoutHint({ hasCap = false }) {
    return (
        <Sheet sx={{ borderRadius: '24px', p: 2 }}>
            <Typography level="title-sm" sx={{ pl: 0.5, mb: 0.5 }}>
                Как выглядит профиль
            </Typography>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ pl: 0.5, mb: 1.5 }}>
                Раскладка зависит от обложки: с ней профиль выстраивается по центру,
                без неё строкой, где слева аватар, а справа текст.
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1.5,
                    // Блок объясняет, а не управляет: ни курсора, ни реакции на клик.
                    pointerEvents: 'none',
                    userSelect: 'none',
                }}
            >
                <LayoutPreview title="С шапкой" active={hasCap}>
                    <WithCap />
                </LayoutPreview>

                <LayoutPreview title="Без шапки" active={!hasCap}>
                    <WithoutCap />
                </LayoutPreview>
            </Box>
        </Sheet>
    );
}

/** Рамка макета с подписью и отметкой «сейчас так». */
function LayoutPreview({ title, active, children }) {
    return (
        <Sheet
            variant="soft"
            sx={{
                borderRadius: '20px',
                p: 1.5,
                border: '2px solid',
                borderColor: active ? 'primary.500' : 'transparent',
                transition: 'border-color .2s ease',
            }}
        >
            <Box sx={{ height: 92, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {children}
            </Box>

            <Typography
                level="body-xs"
                sx={{ mt: 1, textAlign: 'center', fontWeight: active ? 600 : 400 }}
                textColor={active ? 'primary.500' : 'text.tertiary'}
            >
                {title}{active ? ' · сейчас' : ''}
            </Typography>
        </Sheet>
    );
}

/** Полоска-«текст» в макете. */
function Line({ width, dim = false, align = 'center' }) {
    return (
        <Box
            sx={{
                width,
                height: 5,
                borderRadius: '999px',
                backgroundColor: dim ? 'neutral.softActiveBg' : 'neutral.solidBg',
                opacity: dim ? 0.5 : 0.65,
                alignSelf: align === 'center' ? 'center' : 'flex-start',
            }}
        />
    );
}

/** Обложка сверху, аватар и текст по центру — как в профиле с шапкой. */
function WithCap() {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
                sx={{
                    width: '100%',
                    height: 26,
                    borderRadius: '8px',
                    backgroundColor: 'primary.softBg',
                }}
            />
            <Box
                sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    mt: '-13px',
                    border: '2px solid',
                    borderColor: 'background.surface',
                    backgroundColor: 'primary.500',
                }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', mt: 0.75, width: '100%', alignItems: 'center' }}>
                <Line width="55%" />
                <Line width="35%" dim />
            </Box>
        </Box>
    );
}

/** Слева аватар, справа текст с выравниванием по левому краю. */
function WithoutCap() {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    borderRadius: '50%',
                    backgroundColor: 'primary.500',
                }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
                <Line width="80%" align="start" />
                <Line width="55%" dim align="start" />
                <Line width="65%" dim align="start" />
            </Box>
        </Box>
    );
}
